import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';

const BusinessLineContext = createContext(null);

// Líneas de negocio disponibles
export const BUSINESS_LINES = [
  { id: 'all', name: 'Todas las líneas', color: 'bg-gray-500', visible: ['admin', 'master', 'agente', 'asesor', 'aspirante', 'viewer', 'asistente', 'developer'] },
  { id: 'allianz', name: 'Allianz', color: 'bg-blue-600', visible: ['admin', 'master', 'agente', 'asesor', 'aspirante', 'viewer', 'asistente', 'developer'] },
  { id: 'qualitas', name: 'Qualitas', color: 'bg-green-600', visible: ['admin', 'master', 'agente', 'asesor', 'aspirante', 'viewer', 'asistente', 'developer'] },
  { id: 'insignia', name: 'Insignia Life', color: 'bg-red-600', visible: ['admin', 'master', 'agente', 'asesor', 'aspirante', 'viewer', 'asistente', 'developer'] },
  { id: 'flipping', name: 'Bienes Raíces', color: 'bg-amber-600', visible: ['admin'] } // Solo Admin
];

// Mapeo de nombre de aseguradora (campo user.aseguradoras) → id de línea
const ASEGURADORA_TO_LINE_ID = {
  'Allianz': 'allianz',
  'Insignia Life': 'insignia',
  'Quálitas': 'qualitas',
};

// Roles que se filtran por sus aseguradoras configuradas
const AGENT_LIKE_ROLES = ['agente', 'aspirante', 'asesor', 'asistente', 'developer'];

// "Todas mis líneas": opción especial solo para agentes con 2+ aseguradoras propias.
// NO es lo mismo que 'all' (admin, sin restricción) — acota al array de aseguradoras
// del propio agente (ver filterByBusinessLine y el backend, que resuelve 'mine' con
// un OR de business_lines.op('@>') contra cada una de sus líneas, nunca un bypass total).
const MINE_LINE = { id: 'mine', name: 'Todas mis líneas', color: 'bg-slate-500' };

// Get initial value from localStorage or default to 'all'
const getInitialLine = () => {
  const saved = localStorage.getItem('selectedBusinessLine');
  return saved || 'all';
};

export const BusinessLineProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedLine, setSelectedLine] = useState(getInitialLine);

  // Ids de línea que corresponden a las aseguradoras propias del usuario (solo roles tipo agente)
  const myLineIds = useMemo(() => {
    const aseguradoras = user?.aseguradoras;
    if (!aseguradoras || aseguradoras.length === 0) return new Set();
    return new Set(aseguradoras.map(a => ASEGURADORA_TO_LINE_ID[a]).filter(Boolean));
  }, [user?.aseguradoras]);

  // Filtrar líneas visibles según el rol y las aseguradoras del usuario
  const availableLines = useMemo(() => {
    const role = user?.role || 'viewer';
    const byRole = BUSINESS_LINES.filter(line => line.visible.includes(role));

    // Admin y master ven todas las líneas habilitadas por rol (sin filtro adicional)
    if (!AGENT_LIKE_ROLES.includes(role)) return byRole;

    // Roles tipo agente: filtrar por aseguradoras configuradas en el perfil
    if (myLineIds.size === 0) return byRole; // fallback seguro

    // Sin 'all': solo las líneas (1, 2 o 3) que coincidan con sus aseguradoras configuradas
    const own = byRole.filter(line => myLineIds.has(line.id));
    if (own.length === 0) return byRole; // fallback seguro si ninguna aseguradora matchea

    // "Todas mis líneas" solo tiene sentido con 2+ aseguradoras propias
    return myLineIds.size >= 2 ? [MINE_LINE, ...own] : own;
  }, [user?.role, myLineIds]);

  // Validar que la línea seleccionada esté disponible; si no (p.ej. 'all' ya no aplica
  // para el usuario), cae a la primera línea disponible en lugar de forzar 'all'
  const validatedLine = useMemo(() => {
    if (availableLines.find(line => line.id === selectedLine)) {
      return selectedLine;
    }
    return availableLines[0]?.id || 'all';
  }, [selectedLine, availableLines]);

  const changeBusinessLine = useCallback((lineId) => {
    if (lineId === 'mine' || BUSINESS_LINES.find(line => line.id === lineId)) {
      setSelectedLine(lineId);
      localStorage.setItem('selectedBusinessLine', lineId);
    }
  }, []);

  // Función para filtrar datos por línea de negocio
  const filterByBusinessLine = useCallback((items, fieldName = 'business_lines') => {
    if (validatedLine === 'all') return items;
    if (validatedLine === 'mine') {
      if (myLineIds.size === 0) return items;
      return items.filter(item => {
        const itemLines = item[fieldName];
        if (Array.isArray(itemLines)) {
          return itemLines.some(l => myLineIds.has(l));
        }
        return myLineIds.has(itemLines);
      });
    }
    return items.filter(item => {
      const itemLines = item[fieldName];
      if (Array.isArray(itemLines)) {
        return itemLines.includes(validatedLine);
      }
      return itemLines === validatedLine;
    });
  }, [validatedLine, myLineIds]);

  // Obtener info de la línea actual
  const currentLine = useMemo(() => {
    if (validatedLine === 'mine') return MINE_LINE;
    return BUSINESS_LINES.find(line => line.id === validatedLine) || BUSINESS_LINES[0];
  }, [validatedLine]);

  const value = useMemo(() => ({
    selectedLine: validatedLine,
    currentLine,
    availableLines,
    changeBusinessLine,
    filterByBusinessLine,
    isAllLines: validatedLine === 'all',
    BUSINESS_LINES
  }), [validatedLine, currentLine, availableLines, changeBusinessLine, filterByBusinessLine]);

  return (
    <BusinessLineContext.Provider value={value}>
      {children}
    </BusinessLineContext.Provider>
  );
};

export const useBusinessLine = () => {
  const context = useContext(BusinessLineContext);
  if (!context) {
    throw new Error('useBusinessLine must be used within a BusinessLineProvider');
  }
  return context;
};
